import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Draft persistence for every form (LOCKED standard): autosave while typing, offer
 * "Draft restored — Keep / Discard" on return, clear on submit. Never pass secrets
 * (PINs, OTPs, passwords, tokens) — this writes to the browser's local storage.
 *
 * key       `<form>:<id|new>`
 * value     current form state
 * enabled   turn on only after the server record has loaded
 * isEmpty   empty forms are never saved (and an empty value clears the draft)
 */
export interface FormDraft<T> {
  /** A saved draft that differs from the server value, waiting for Keep or Discard. */
  restored: T | null;
  keep(): T | null;
  discard(): void;
  clear(): void;
}

const PREFIX = 'capsico:draft:';

function read<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: drafts are a convenience, never a requirement.
  }
}

function remove(key: string): void {
  try {
    window.localStorage.removeItem(PREFIX + key);
  } catch {
    // ignore
  }
}

export function useFormDraft<T>(
  key: string,
  value: T,
  options: { enabled: boolean; isEmpty: (value: T) => boolean; base?: T },
): FormDraft<T> {
  const { enabled, isEmpty, base } = options;
  const [restored, setRestored] = useState<T | null>(null);
  const checkedKey = useRef<string | null>(null);
  const skipNextSave = useRef(false);

  // On first enable for this key: surface a stored draft that differs from the server value.
  useEffect(() => {
    if (!enabled || checkedKey.current === key) return;
    checkedKey.current = key;
    const stored = read<T>(key);
    if (stored !== null && JSON.stringify(stored) !== JSON.stringify(base ?? value)) {
      setRestored(stored);
    } else {
      setRestored(null);
    }
    skipNextSave.current = true;
  }, [enabled, key, base, value]);

  // Autosave (debounced) while the user edits.
  useEffect(() => {
    // While a restored draft waits for Keep / Discard, never overwrite or delete it.
    if (!enabled || checkedKey.current !== key || restored !== null) return;
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }
    const timer = window.setTimeout(() => {
      if (isEmpty(value) || JSON.stringify(value) === JSON.stringify(base)) remove(key);
      else write(key, value);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [enabled, key, value, base, isEmpty, restored]);

  const keep = useCallback(() => {
    const draft = restored;
    setRestored(null);
    return draft;
  }, [restored]);

  const discard = useCallback(() => {
    remove(key);
    setRestored(null);
  }, [key]);

  const clear = useCallback(() => {
    remove(key);
    setRestored(null);
  }, [key]);

  return { restored, keep, discard, clear };
}
